import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { isTalentPartner, isTalentPartnerOnCreator } from '../flowUser';

/**
 * Who the influencer flow acts for — the signed-in creator (UserReducer).
 *
 * The app reads `user.influencer_idd` (lnk_account_influencers, the sign-in
 * query's alias); the web's sign-in row carries the same value, plus
 * `influencer_id` (auth_influencers) — the first present one is the creator,
 * as the app's zapInfluencerId does. These are what the brd API checks against
 * the caller's token (assertInfluencer / assertAccount), so never 0.
 *
 * A Talent Partner acts for the creator picked on /switch/user: that creator is
 * `influencer_id` (`influencer_idd` stays the Talent Partner's own row) and the
 * name is the creator's. Before a pick, `influencerId` is 0 (flowUser.ts).
 */
export function useInfluencerUser() {
  const user = useSelector((state: any) => state?.UserReducer?.UserData) || null;
  const owner = useSelector((state: any) => state?.SwitchLayer2Reducer?.switchUser) || null;
  return useMemo(() => {
    const u: any = user || {};
    const num = (v: unknown) => {
      const n = Number(v);
      return Number.isFinite(n) && n > 0 ? n : 0;
    };
    const talentPartner = isTalentPartner(u);
    const onCreator = isTalentPartnerOnCreator(u, owner);
    return {
      influencerId: talentPartner ? (onCreator ? num(u.influencer_id) : 0) : num(u.influencer_idd) || num(u.influencer_id),
      accountId: num(u.account_id),
      userId: num(u.user_id),
      /** The creator's gender ('Male' / 'Female' …), for a brief open to one gender only. */
      gender: String(u.gender || '').trim(),
      /** The sender name on the creator's messages (as the app sends it). */
      displayName: String((talentPartner ? u.name : '') || u.account_name || u.full_name || '').trim(),
      isInfluencer: String(u.account_type || '') === 'Influencer',
      /** A Talent Partner (acting for `influencerId`, the picked creator, when it is not 0). */
      isTalentPartner: talentPartner,
      user: u,
    };
  }, [user, owner]);
}

export type InfluencerUser = ReturnType<typeof useInfluencerUser>;
